import React, { useState, useEffect } from "react";
import { useAppSelector } from "../../../store/hooks";
import {
  selectHullAndArmorStats,
  selectors as shipBuilderSelectors,
} from "../../../store/selectors/shipBuilderSelectors";
import { useDispatch } from "react-redux";
import { setShipName, setShipClass } from "../../../store";
import { getSoftwarePackage } from "../../../utils/calculations/softwareCalculations";
import * as derivedSelectors from "../../../store/selectors/derivedSelectors";
import { formatCurrency } from "../../../utils/formatting";
import {
  groupSystems,
  isGroupedSystem,
} from "../../../utils/helpers/systemHelpers";
import { AccommodationSystem } from "../../../types/optionalSystems";
import { CrewRole } from "../../../types/crew";
import { ShipImageUpload } from "./ShipImageUpload";
import { DeckPlanUpload } from "./DeckPlanUpload";
import { SaveLoadButtons } from "../../SaveLoadButtons";
import { ResetButton } from "../../ResetButton";
import { printShipSheet } from "../../../utils/printUtils";
import { BreakawayHullsSummary } from "./BreakawayHullsSummary";

const ARMOR_TYPE_LABELS: Record<string, string> = {
  titaniumSteel: "Titanium Steel Armor",
  crystaliron: "Crystaliron Armor",
  bondedSuperdense: "Bonded Superdense Armor",
  molecularBonded: "Molecular Bonded Armor",
};

export const ShipSheet: React.FC = () => {
  const [shipName, setShipNameState] = useState("");
  const [shipClass, setShipClassState] = useState("");
  const hullAndArmorStats = useAppSelector(selectHullAndArmorStats);
  const hullPoints = hullAndArmorStats?.hullPoints ?? "";
  const hull = useAppSelector(shipBuilderSelectors.selectHull);
  const armor = hull?.armor ?? null;
  const armorTypeLabel = armor?.type
    ? ARMOR_TYPE_LABELS[armor.type] || armor.type
    : "";
  const protectionLevel = armor?.rating ?? "";

  const finalDetails = useAppSelector(shipBuilderSelectors.selectFinalDetails);
  useEffect(() => {
    setShipNameState(finalDetails?.name || "");
    setShipClassState(finalDetails?.class || "");
  }, [finalDetails?.name, finalDetails?.class]);

  const dispatch = useDispatch();

  // Helper to construct the class string from hull selections
  const classParts: string[] = [];
  if (hull?.configuration?.name) classParts.push(hull.configuration.name);
  if (hull?.specializedType?.name) classParts.push(hull.specializedType.name);
  if (hull?.additionalType?.name) {
    let addName = hull.additionalType.name;
    if (hull.additionalType.percent) {
      addName += ` (${hull.additionalType.percent}%)`;
    }
    classParts.push(addName);
  }
  const constructedClassString =
    classParts.length === 0 ? "Standard Hull" : classParts.join(", ");

  // Calculate input width based on user input length (min 10ch, max 32ch)
  const classInputWidth = Math.max(10, Math.min(shipClass.length + 2, 32));

  // Round hull points for display
  let displayHullPoints = "";
  if (typeof hullPoints === "number") {
    displayHullPoints = Number.isInteger(hullPoints)
      ? hullPoints.toString()
      : hullPoints.toFixed(1);
  } else if (typeof hullPoints === "string") {
    displayHullPoints = hullPoints;
  }

  const hullSize = hull?.size || 0;
  const techLevel = useAppSelector(shipBuilderSelectors.selectTechLevel);
  const isVowel = (n: number) => ["8"].includes(n.toString()[0]);
  const article = isVowel(hullSize) ? "An" : "A";
  const tonType = hullSize < 100 ? "Small Craft" : "Starship";
  const step0String = `${article} ${hullSize} Ton ${tonType} of Technology Level ${techLevel}`;
  const nameInputWidth = Math.max(10, Math.min(shipName.length + 2, 32));

  const drives = useAppSelector((state) => state.shipBuilder.drives);
  const effectiveDriveRatings = useAppSelector(
    derivedSelectors.selectEffectiveDriveRatings,
  ) as {
    hasExternalCargo: boolean;
    hasJumpNet: boolean;
    baseManeuverRating: number;
    effectiveManeuverRating: number;
    baseJumpRating: number;
    effectiveJumpRating: number;
    baseReactionRating: number;
    effectiveReactionRating: number;
    effectiveTonnage: number;
    externalCargoTonnage: number;
    jumpNetCargoTonnage: number;
  };



  // Get power requirements from Redux state
  const bridge = useAppSelector((state) => state.shipBuilder.bridge);
  const sensors = useAppSelector((state) => state.shipBuilder.sensors);
  const weaponMounts = useAppSelector(
    (state) => state.shipBuilder.weaponMounts,
  );
  const optionalSystems = useAppSelector((state) => state.optionalSystems);
  const stateroomConfig = useAppSelector(
    (state) => state.shipBuilder.stateroomConfig,
  );
  const passengerConfig = useAppSelector(
    (state) => state.shipBuilder.passengerConfig,
  );

  const powerPlant = useAppSelector((state) => state.shipBuilder.powerPlant);
  const software = useAppSelector((state) => state.shipBuilder.software);
  const computer = useAppSelector((state) => state.shipBuilder.computer);

  const crewMode = useAppSelector((state) => state.shipBuilder.crewMode);
  const crewOverrides = useAppSelector(
    (state) => state.shipBuilder.crewOverrides,
  );
  const fuelTanks = useAppSelector((state) => state.shipBuilder.fuelTanks || 0);

  // Calculate power values
  const basicSystemsPower = Math.ceil(hullSize * 0.2); // 20% of hull tonnage
  const bridgePower = bridge?.power || 0;
  const sensorsPower = sensors?.power || 0;

  // Calculate weapons and defence power
  const weaponsPower = (weaponMounts || []).reduce(
    (total, mount) => total + (mount.power || 0),
    0,
  );
  const defensiveSystems = useAppSelector(
    (state) => state.shipBuilder.defensiveSystems,
  );
  const defensiveSystemsPower = (defensiveSystems || []).reduce(
    (total: number, system: any) => total + (system.power || 0),
    0,
  );
  const weaponsAndDefencePower = weaponsPower + defensiveSystemsPower;

  // Calculate optional systems power
  const optionalSystemsPower = useAppSelector((state) => {
    const allOptionalSystems = [
      ...(Array.isArray(state.optionalSystems.structure)
        ? state.optionalSystems.structure
        : []),
      ...(Array.isArray(state.optionalSystems.modularHull)
        ? state.optionalSystems.modularHull
        : []),
      ...(Array.isArray(state.optionalSystems.power)
        ? state.optionalSystems.power
        : []),
      ...(Array.isArray(state.optionalSystems.drives)
        ? state.optionalSystems.drives
        : []),
      ...(Array.isArray(state.optionalSystems.fuel)
        ? state.optionalSystems.fuel
        : []),
      ...(Array.isArray(state.optionalSystems.accommodations)
        ? state.optionalSystems.accommodations
        : []),
      ...(Array.isArray(state.optionalSystems.bridge)
        ? state.optionalSystems.bridge
        : []),
      ...(Array.isArray(state.optionalSystems.cargo)
        ? state.optionalSystems.cargo
        : []),
      ...(Array.isArray(state.optionalSystems.drones)
        ? state.optionalSystems.drones
        : []),
      ...(Array.isArray(state.optionalSystems.sensors)
        ? state.optionalSystems.sensors
        : []),
      ...(Array.isArray(state.optionalSystems.external)
        ? state.optionalSystems.external
        : []),
      ...(Array.isArray(state.optionalSystems.internal)
        ? state.optionalSystems.internal
        : []),
      ...(Array.isArray(state.optionalSystems.software)
        ? state.optionalSystems.software
        : []),
      ...(Array.isArray(state.optionalSystems.commonAreas)
        ? state.optionalSystems.commonAreas
        : []),
    ];
    return (allOptionalSystems || []).reduce((total: number, system: any) => {
      if (system.type === "modularHull" && Array.isArray(system.modules)) {
        const fitoutPower = (system.modules || [])
          .filter((m: any) => m.installed)
          .reduce((sum: number, m: any) => sum + (m.fitoutPower || 0), 0);
        return total + fitoutPower;
      }
      return total + (system.power || 0);
    }, 0);
  });

  // Calculate staterooms power
  const stateroomsPower = (() => {
    if (!stateroomConfig) return 0;

    return (
      // Staterooms power (0 for standard)
      (stateroomConfig.single || 0) * 0 + // Standard staterooms have 0 power
      (stateroomConfig.double || 0) * 0 + // Standard staterooms have 0 power
      // Low berths power (1 per 10 berths)
      Math.ceil((stateroomConfig.lowBerths?.count || 0) / 10) +
      // Emergency low berths power
      (stateroomConfig.emergencyLowBerths?.count || 0) * 1 + // 1 power each
      // Common area power
      (stateroomConfig.commonArea || 0) * 0.5 // 0.5 power per ton
    );
  })();

  const maneuverDrivePower = drives.find((d) => d.type === "maneuver")?.rating
    ? Math.ceil(
        hullSize * 0.1 * drives.find((d) => d.type === "maneuver")!.rating,
      )
    : 0;
  const jumpDrivePower = drives.find((d) => d.type === "jump")?.rating
    ? Math.ceil(hullSize * 0.1 * drives.find((d) => d.type === "jump")!.rating)
    : 0;

  // Cost selectors
  const fullRefuelCost = useAppSelector(
    derivedSelectors.selectFullRefuelCostRefined,
  ) as number;
  const mortgage = useAppSelector(derivedSelectors.selectMortgage) as number;
  const lifeSupportCost = useAppSelector(
    derivedSelectors.selectLifeSupportCost,
  ) as number;
  const crewSalaries = useAppSelector(
    shipBuilderSelectors.selectCrewSalaryTotal,
  ) as number;
  const maintenanceCost = useAppSelector(
    derivedSelectors.selectMaintenanceCost,
  ) as number;
  const totalShipCost = useAppSelector(
    shipBuilderSelectors.selectTotalCost,
  ) as number;

  // Add this with the other selectors at the top of the component
  const smallCraft = useAppSelector((state) => state.shipBuilder.smallCraft || []);

  const supplyUnits = useAppSelector(shipBuilderSelectors.selectSupplyUnits);
  const supplyTons = useAppSelector(shipBuilderSelectors.selectSupplyTons);
  const ammunitionStorage = useAppSelector((state) => state.shipBuilder.ammunitionStorage || []);

  return (
    <div id="print-container" className="bg-white dark:bg-gray-800 p-6 md:p-10 rounded-lg shadow w-full border border-gray-300 dark:border-gray-700 print:border-0 print:shadow-none">
      {/* Save/Load/Reset/Print Buttons */}
      <div className="mb-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <div className="lg:col-span-2">
            <SaveLoadButtons variant="full" />
          </div>
          <div className="flex gap-2">
            <ResetButton variant="full" />
            <button
              onClick={printShipSheet}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors font-medium"
            >
              🖨️ Print Ship Sheet
            </button>
          </div>
        </div>
      </div>
      
      {/* Top Row: Ship Name, Hull Points, Armour */}
      <div className="flex flex-wrap gap-4 mb-4 items-start">
        {/* Ship's Name and Class (stacked) */}
        <div className="flex-1 min-w-[300px] flex flex-col gap-2">
          <div>
            <div className="bg-blue-200 dark:bg-blue-800 text-blue-900 dark:text-blue-100 font-bold px-3 py-1 rounded-t-md text-xs tracking-wide">
              SHIP'S NAME
            </div>
            <div className="flex items-center w-full border border-t-0 border-blue-200 dark:border-blue-700 rounded-b-md bg-white dark:bg-gray-700">
              <input
                type="text"
                className="px-3 py-2 h-10 focus:outline-none focus:ring-2 focus:ring-blue-300 text-base transition-all duration-200 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 flex-shrink-0 min-w-[220px]"
                style={{ width: `${nameInputWidth}ch` }}
                placeholder="Enter ship's name"
                value={shipName}
                onChange={(e) => {
                  setShipNameState(e.target.value);
                  dispatch(setShipName(e.target.value));
                }}
              />
              <div className="flex-1 flex justify-end overflow-hidden">
                <span
                  className="px-3 py-2 h-10 flex items-center text-base text-gray-900 dark:text-gray-100 whitespace-nowrap text-right bg-white dark:bg-gray-700 mr-2 truncate"
                  title="Ship summary"
                >
                  {step0String}
                </span>
              </div>
            </div>
          </div>
          <div>
            <div className="bg-blue-200 dark:bg-blue-800 text-blue-900 dark:text-blue-100 font-bold px-3 py-1 rounded-t-md text-xs tracking-wide">
              CLASS
            </div>
            <div className="flex items-center w-full border border-t-0 border-blue-200 dark:border-blue-700 rounded-b-md bg-white dark:bg-gray-700">
              <input
                type="text"
                className="px-3 py-2 h-10 focus:outline-none focus:ring-2 focus:ring-blue-300 text-base transition-all duration-200 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 flex-shrink-0 min-w-[220px]"
                style={{ width: `${classInputWidth}ch` }}
                placeholder="Enter ship class"
                value={shipClass}
                onChange={(e) => {
                  setShipClassState(e.target.value);
                  dispatch(setShipClass(e.target.value));
                }}
              />
              <div className="flex-1 flex justify-end">
                <span
                  className="px-3 py-2 h-10 flex items-center text-base text-gray-900 dark:text-gray-100 whitespace-nowrap text-right bg-white dark:bg-gray-700 mr-2"
                  title="Hull configuration summary"
                >
                  {constructedClassString}
                </span>
              </div>
            </div>
          </div>
        </div>
        {/* Hull Points and Armour (vertical stack) */}
        <div className="flex flex-col gap-2 min-w-[120px]">
          <div>
            <div className="bg-orange-400 text-white font-bold px-3 py-1 rounded-t-md text-xs tracking-wide text-center">
              HULL POINTS
            </div>
            <div className="border border-t-0 border-orange-400 rounded-b-md px-3 py-2 h-10 text-center text-lg font-semibold text-gray-900 dark:text-gray-100">
              {displayHullPoints}
            </div>
          </div>
          <div>
            <div className="bg-orange-400 text-white font-bold px-3 py-1 rounded-t-md text-xs tracking-wide text-center">
              ARMOUR
            </div>
            <div className="border border-t-0 border-orange-400 rounded-b-md px-3 py-2 h-14 text-center flex flex-col items-center justify-center">
              <span className="text-xs text-gray-500 dark:text-gray-400 leading-tight">
                {armorTypeLabel}
              </span>
              <span className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                {protectionLevel}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Ship Image Upload Section */}
      <div className="mb-6">
        <ShipImageUpload />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="flex flex-col gap-4">
          {/* Costs Section - move this above the computer/software section */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="bg-orange-400 text-white font-bold px-2 py-1 rounded-t-md text-xs text-center">
                FUEL (FULL TANK) COST
              </div>
              <div className="border border-t-0 border-orange-400 rounded-b-md px-2 py-1 text-xs text-center">
                {fullRefuelCost > 0 ? formatCurrency(fullRefuelCost) : "—"}
              </div>
            </div>
            <div>
              <div className="bg-orange-400 text-white font-bold px-2 py-1 rounded-t-md text-xs text-center">
                MORTGAGE
              </div>
              <div className="border border-t-0 border-orange-400 rounded-b-md px-2 py-1 text-xs text-center">
                {mortgage > 0 ? formatCurrency(mortgage) : "—"}
              </div>
            </div>
            <div>
              <div className="bg-orange-400 text-white font-bold px-2 py-1 rounded-t-md text-xs text-center">
                LIFE SUPPORT
              </div>
              <div className="border border-t-0 border-orange-400 rounded-b-md px-2 py-1 text-xs text-center">
                {lifeSupportCost > 0 ? formatCurrency(lifeSupportCost) : "—"}
              </div>
            </div>
            <div>
              <div className="bg-orange-400 text-white font-bold px-2 py-1 rounded-t-md text-xs text-center">
                SALARIES
              </div>
              <div className="border border-t-0 border-orange-400 rounded-b-md px-2 py-1 text-xs text-center">
                {crewSalaries > 0 ? formatCurrency(crewSalaries) : "—"}
              </div>
            </div>
            <div className="col-span-2">
              <div className="bg-orange-400 text-white font-bold px-2 py-1 rounded-t-md text-xs text-center">
                COST PER MAINTENANCE PERIOD
              </div>
              <div className="border border-t-0 border-orange-400 rounded-b-md px-2 py-1 text-xs text-center">
                {maintenanceCost > 0 ? formatCurrency(maintenanceCost) : "—"}
              </div>
            </div>
          </div>

          {/* Purchase Cost Section */}
          <div>
            <div className="bg-red-500 text-white font-bold px-2 py-1 rounded-t-md text-xs text-center">
              PURCHASE COST
            </div>
            <div className="border border-t-0 border-red-500 rounded-b-md px-2 py-1 text-xs text-center">
              {totalShipCost > 0 ? formatCurrency(totalShipCost) : "—"}
            </div>
          </div>
          {/* Ship's Computer Section - now below costs */}
          <div>
            <div className="bg-blue-200 text-blue-900 font-bold px-3 py-1 rounded-t-md text-xs tracking-wide">
              SHIP'S COMPUTER
            </div>
            <div className="border border-t-0 border-blue-200 rounded-b-md p-3">
              {/* Computer Systems */}
              <div className="text-xs text-gray-700 space-y-1 mb-3">
                {computer?.primary && (
                  <div className="flex justify-between items-center border-b border-dashed border-gray-300 py-1">
                    <span>Primary: {computer.primary.model}</span>
                    <span className="text-xs text-gray-500">
                      {computer.primary.options.includes("jumpControl")
                        ? "/bis"
                        : ""}
                      {computer.primary.options.includes("hardened")
                        ? "/fib"
                        : ""}
                    </span>
                  </div>
                )}
                {computer?.backup && (
                  <div className="flex justify-between items-center border-b border-dashed border-gray-300 py-1">
                    <span>Secondary: {computer.backup.model}</span>
                    <span className="text-xs text-gray-500">
                      {computer.backup.options.includes("jumpControl")
                        ? "/bis"
                        : ""}
                      {computer.backup.options.includes("hardened")
                        ? "/fib"
                        : ""}
                    </span>
                  </div>
                )}
              </div>

              {/* Software Packages */}
              <div className="flex justify-between items-center mb-2">
                <span className="font-semibold text-xs">
                  SOFTWARE PACKAGES:
                </span>
                <span className="bg-orange-400 text-white font-bold px-2 py-1 rounded text-xs ml-2">
                  BANDWIDTH
                </span>
              </div>
              <div className="text-xs text-gray-700 space-y-1">
                {software?.installed?.map((pkg, index) => {
                  const softwarePackage = getSoftwarePackage(pkg.packageId);
                  let bandwidth = 0;
                  let displayName = pkg.packageId;

                  if (softwarePackage) {
                    displayName = softwarePackage.name;
                    if (softwarePackage.levels) {
                      // Multi-level software - get bandwidth from level
                      const level = softwarePackage.levels.find(
                        (l) => l.level === pkg.level,
                      );
                      bandwidth = level?.bandwidth || 0;
                    } else {
                      // Single-level software - use base bandwidth
                      bandwidth = softwarePackage.bandwidth || 0;
                    }
                  }

                  const levelText =
                    pkg.level > 0 ? ` (Level ${pkg.level})` : "";

                  return (
                    <div
                      key={index}
                      className="flex justify-between items-center border-b border-dashed border-gray-300 py-1"
                    >
                      <span>
                        {displayName}
                        {levelText}
                      </span>
                      <span className="font-semibold">{bandwidth}</span>
                    </div>
                  );
                }) ||
                  // Fallback empty rows if no software installed
                  [...Array(5)].map((_, i) => (
                    <div
                      key={i}
                      className="h-4 border-b border-dashed border-gray-300"
                    />
                  ))}
              </div>
            </div>
          </div>
        </div>

        {/* Center Column */}
        <div className="flex flex-col gap-4">
          {/* Assigned Crew Summary Box */}
          <div>
            <div className="bg-blue-100 text-blue-900 font-bold px-3 py-1 rounded-t-md text-xs tracking-wide">
              CREW SUMMARY{crewMode ? ` (${crewMode})` : ""}
            </div>
            <div className="border border-t-0 border-blue-100 rounded-b-md p-3 text-xs">
              {(() => {
                // Full list of roles and their salaries
                const roles = [
                  { label: "Captain", key: "Captain", salary: 10000 },
                  { label: "Pilot", key: "Pilot", salary: 6000 },
                  { label: "Astrogator", key: "Astrogator", salary: 5000 },
                  { label: "Engineer", key: "Engineer", salary: 4000 },
                  { label: "Maintenance", key: "Maintenance", salary: 1000 },
                  { label: "Gunner", key: "Gunner", salary: 2000 },
                  { label: "Steward", key: "Steward", salary: 2000 },
                  {
                    label: "Administrator",
                    key: "Administrator",
                    salary: 1500,
                  },
                  {
                    label: "Sensor Operator",
                    key: "Sensor Operator",
                    salary: 4000,
                  },
                  { label: "Medic", key: "Medic", salary: 4000 },
                  { label: "Officer", key: "Officer", salary: 5000 },
                  {
                    label: "Troops/Marines",
                    key: "Ship's Troops/Marines",
                    salary: 1000,
                  },
                ];
                // Only show roles with assigned > 0
                const visibleRoles = roles.filter((r) => {
                  // Ensure crewOverrides exists
                  if (!crewOverrides) return false;
                  
                  // Handle special case for "Ship's Troops/Marines"
                  if (r.key === "Ship's Troops/Marines") {
                    return (crewOverrides["Ship's Troops/Marines" as keyof typeof crewOverrides] ?? 0) > 0;
                  }
                  
                  // Convert string key to enum value for crewOverrides access
                  const roleEnum = Object.values(CrewRole).find(role => role === r.key) as CrewRole;
                  if (!roleEnum) return false;
                  
                  return (crewOverrides[roleEnum] ?? 0) > 0;
                });
                return (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {visibleRoles.map((role) => (
                      <div
                        key={role.key}
                        className="flex items-center justify-between gap-2 min-w-0 max-w-full p-1"
                      >
                        <span className="text-gray-700 text-xs truncate flex-1 min-w-0">
                          {role.label}:
                        </span>
                        <span className="font-semibold text-gray-900 text-xs flex-shrink-0 ml-2">
                          {
                            (() => {
                              // Ensure crewOverrides exists
                              if (!crewOverrides) return 0;
                              
                              // Handle special case for "Ship's Troops/Marines"
                              if (role.key === "Ship's Troops/Marines") {
                                return crewOverrides["Ship's Troops/Marines" as keyof typeof crewOverrides] ?? 0;
                              }
                              
                              // Convert string key to enum value for crewOverrides access
                              const roleEnum = Object.values(CrewRole).find(r => r === role.key) as CrewRole;
                              return roleEnum ? (crewOverrides[roleEnum] ?? 0) : 0;
                            })()
                          }
                        </span>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
          {/* Power Requirement */}
          <div>
            <div className="bg-gray-100 text-gray-700 font-bold px-3 py-1 rounded-t-md text-xs tracking-wide text-center">
              POWER REQUIREMENT
            </div>
            <div className="border border-t-0 border-gray-200 rounded-b-md p-3 flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-xs">POWER POINTS</span>
                <span className="border-b border-gray-400 flex-1 px-2 py-1 text-right font-semibold">
                  {powerPlant?.power || 0}
                </span>
              </div>
              <div className="flex flex-col gap-1 mt-2 text-xs">
                <div>
                  BASIC SHIP SYSTEMS ▶{" "}
                  {basicSystemsPower > 0 ? basicSystemsPower : "—"}
                </div>
                <div>
                  JUMP DRIVE ▶ {jumpDrivePower > 0 ? jumpDrivePower : "—"}
                </div>
                <div>
                  WEAPONS ▶{" "}
                  {weaponsAndDefencePower > 0 ? weaponsAndDefencePower : "—"}
                </div>
                <div>
                  MANOEUVRE DRIVE ▶{" "}
                  {maneuverDrivePower > 0 ? maneuverDrivePower : "—"}
                </div>
                <div>SENSORS ▶ {sensorsPower > 0 ? sensorsPower : "—"}</div>
                <div>BRIDGE ▶ {bridgePower > 0 ? bridgePower : "—"}</div>
                <div>
                  OPTIONAL SYSTEMS ▶{" "}
                  {optionalSystemsPower > 0 ? optionalSystemsPower : "—"}
                </div>
                <div>
                  STATEROOMS ▶ {stateroomsPower > 0 ? stateroomsPower : "—"}
                </div>
              </div>
            </div>
          </div>
          {/* Drives */}
          <div>
            <div className="bg-blue-200 text-blue-900 font-bold px-3 py-1 rounded-t-md text-xs tracking-wide">
              DRIVES
            </div>
            <div className="border border-t-0 border-blue-200 rounded-b-md p-3 text-xs">
              <div className="flex flex-col gap-2">
                {/* Full Drive Potential */}
                <div>
                  <div className="font-semibold text-blue-800 mb-1">Full Drive Potential:</div>
                  <div className="flex flex-col gap-1 ml-2">
                    <div>
                      MANOEUVRE DRIVE ▶ THRUST:{" "}
                      <span className="text-center">
                        {effectiveDriveRatings.baseManeuverRating > 0 ? effectiveDriveRatings.baseManeuverRating : "—"}
                      </span>
                    </div>
                    <div>
                      REACTION DRIVE ▶ THRUST:{" "}
                      <span className="text-center">
                        {effectiveDriveRatings.baseReactionRating > 0 ? effectiveDriveRatings.baseReactionRating : "—"}
                      </span>
                    </div>
                    <div>
                      JUMP DRIVE ▶ JUMP:{" "}
                      <span className="text-center">
                        {effectiveDriveRatings.baseJumpRating > 0 ? effectiveDriveRatings.baseJumpRating : "—"}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Effective Drive Power */}
                <div>
                  <div className="font-semibold text-blue-800 mb-1">Effective Drive Power:</div>
                  <div className="flex flex-col gap-1 ml-2">
                    <div>
                      MANOEUVRE DRIVE ▶ THRUST:{" "}
                      <span className="text-center">
                        {effectiveDriveRatings.effectiveManeuverRating > 0 ? effectiveDriveRatings.effectiveManeuverRating : "—"}
                      </span>
                    </div>
                    <div>
                      REACTION DRIVE ▶ THRUST:{" "}
                      <span className="text-center">
                        {effectiveDriveRatings.effectiveReactionRating > 0 ? effectiveDriveRatings.effectiveReactionRating : "—"}
                      </span>
                    </div>
                    <div>
                      JUMP DRIVE ▶ JUMP:{" "}
                      <span className="text-center">
                        {effectiveDriveRatings.effectiveJumpRating > 0 ? effectiveDriveRatings.effectiveJumpRating : "—"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* External Cargo and Jump Net Warning */}
          {/* Removed as per user request */}
        </div>

        {/* Right Column */}
        <div className="flex flex-col gap-4">
          {/* Bridge Configuration Box */}
          <div>
            <div className="bg-blue-100 text-blue-900 font-bold px-3 py-1 rounded-t-md text-xs tracking-wide">
              BRIDGE CONFIGURATION
            </div>
            <div className="border border-t-0 border-blue-100 rounded-b-md p-3 text-xs">
              <div className="flex flex-col gap-1">
                <div>
                  <span className="font-semibold">Type:</span>{" "}
                  {bridge?.type || "—"}
                </div>
                {bridge?.special && (
                  <div>
                    <span className="font-semibold">Special:</span>{" "}
                    {bridge.special}
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Sensors Table */}
          <div className="sensors-section">
            <div className="bg-blue-200 text-blue-900 font-bold px-3 py-1 rounded-t-md text-xs tracking-wide">
              SENSORS
            </div>
            <table className="w-full border border-blue-200 rounded-b-md text-xs">
              <thead>
                <tr className="bg-blue-50">
                  <th className="p-1 border">TYPE</th>
                  <th className="p-1 border">DM</th>
                </tr>
              </thead>
              <tbody>
                {sensors ? (
                  <tr>
                    <td className="border p-1 h-6 text-xs">{sensors.name}</td>
                    <td className="border p-1 h-6 text-center text-xs">
                      {sensors.dm > 0 ? `+${sensors.dm}` : sensors.dm}
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td className="border p-1 h-6 text-xs text-gray-400">
                      No sensors
                    </td>
                    <td className="border p-1 h-6 text-center text-xs text-gray-400">
                      —
                    </td>
                  </tr>
                )}

                {/* Add-on sensor arrays (Extended Array, Rapid Deployment Extended Array, Distributed Array) */}
                {optionalSystems.sensors &&
                  optionalSystems.sensors.length > 0 &&
                  optionalSystems.sensors
                    .filter((system) =>
                      [
                        "extendedArray",
                        "rapidDeploymentExtendedArray",
                        "distributedArray",
                      ].includes(system.subtype),
                    )
                    .map((system, index) => (
                      <tr key={`sensor-addon-${index}`}>
                        <td className="border p-1 h-6 text-xs pl-4">
                          ▶ {system.name}
                        </td>
                        <td className="border p-1 h-6 text-center text-xs">
                          —
                        </td>
                      </tr>
                    ))}

                {/* Other optional sensor systems */}
                {optionalSystems.sensors &&
                  optionalSystems.sensors.length > 0 &&
                  optionalSystems.sensors
                    .filter(
                      (system) =>
                        ![
                          "extendedArray",
                          "rapidDeploymentExtendedArray",
                          "distributedArray",
                        ].includes(system.subtype),
                    )
                    .map((system, index) => {
                      // Get DM value based on system type
                      let dmValue = "—";
                      let displayName = system.name;

                      if (
                        system.subtype === "countermeasuresSuite" &&
                        system.properties?.jamDM
                      ) {
                        dmValue = `+${system.properties.jamDM}`;
                      } else if (
                        system.subtype === "militaryCountermeasuresSuite" &&
                        system.properties?.jamDM
                      ) {
                        dmValue = `+${system.properties.jamDM}`;
                      } else if (
                        system.subtype === "improvedSignalProcessing" &&
                        system.properties?.sensorDM
                      ) {
                        dmValue = `+${system.properties.sensorDM}`;
                        displayName = `${system.name} (DM applies to all sensor checks)`;
                      } else if (
                        system.subtype === "enhancedSignalProcessing" &&
                        system.properties?.sensorDM
                      ) {
                        dmValue = `+${system.properties.sensorDM}`;
                        displayName = `${system.name} (DM applies to all sensor checks)`;
                      }

                      return (
                        <tr key={`sensor-other-${index}`}>
                          <td className="border p-1 h-6 text-xs">
                            {displayName}
                          </td>
                          <td className="border p-1 h-6 text-center text-xs">
                            {dmValue}
                          </td>
                        </tr>
                      );
                    })}

                {/* Add empty rows to maintain table height */}
                {(() => {
                  const totalRows =
                    (sensors ? 1 : 0) +
                    (optionalSystems.sensors?.filter((s) =>
                      [
                        "extendedArray",
                        "rapidDeploymentExtendedArray",
                        "distributedArray",
                      ].includes(s.subtype),
                    ).length || 0) +
                    (optionalSystems.sensors?.filter(
                      (s) =>
                        ![
                          "extendedArray",
                          "rapidDeploymentExtendedArray",
                          "distributedArray",
                        ].includes(s.subtype),
                    ).length || 0);
                  return [...Array(Math.max(0, 3 - totalRows))].map((_, i) => (
                    <tr key={`empty-${i}`}>
                      <td className="border p-1 h-6" />
                      <td className="border p-1 h-6" />
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>
          {/* Modular Hull Section */}
          {(() => {
            const modularHullSystems = Array.isArray(
              optionalSystems.modularHull,
            )
              ? optionalSystems.modularHull
              : [];
            if (modularHullSystems.length === 0) return null;

            const modularHull = modularHullSystems[0]; // Should only be one modular hull system
            const totalModularTonnage = modularHull.modularTonnage || 0;
            const modularPercent = modularHull.modularPercent || 0;
            const installedModules =
              modularHull.modules?.filter((m: any) => m.installed) || [];
            const availableModules =
              modularHull.modules?.filter((m: any) => !m.installed) || [];
            const totalInstalledWeight = installedModules.reduce(
              (sum: number, m: any) => sum + (m.fitoutWeight || 0),
              0,
            );

            const totalAvailableWeight =
              totalModularTonnage - totalInstalledWeight;

            return (
              <div>
                <div className="bg-green-200 text-green-900 font-bold px-3 py-1 rounded-t-md text-xs tracking-wide">
                  MODULAR HULL
                </div>
                <div className="border border-t-0 border-green-200 rounded-b-md p-3">
                  <div className="space-y-2 text-xs">
                    {/* Modular Hull Summary */}
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-800">
                        Modular Space:
                      </span>
                      <span className="text-gray-600">
                        {modularPercent}% ({totalModularTonnage} tons)
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-800">
                        Installed Modules:
                      </span>
                      <span className="text-gray-600">
                        {installedModules.length} modules
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-800">
                        Used Weight:
                      </span>
                      <span className="text-gray-600">
                        {totalInstalledWeight.toFixed(1)} tons
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-800">
                        Available Weight:
                      </span>
                      <span className="text-gray-600">
                        {totalAvailableWeight.toFixed(1)} tons
                      </span>
                    </div>

                    {/* Installed Modules List */}
                    {installedModules.length > 0 && (
                      <div className="mt-3 pt-2 border-t border-gray-200">
                        <div className="font-semibold text-gray-800 mb-2">
                          Installed Modules:
                        </div>
                        <div className="space-y-2">
                          {installedModules.map(
                            (module: any, index: number) => (
                              <div key={index} className="text-xs">
                                <div className="font-medium text-gray-700">
                                  {module.name}
                                </div>
                                <div className="text-gray-600 ml-2">
                                  Weight: {module.fitoutWeight?.toFixed(1) || 0}{" "}
                                  tons | Cost:{" "}
                                  {formatCurrency(module.fitoutCost || 0)}
                                </div>
                                {module.description && (
                                  <div className="text-gray-500 ml-2 mt-1 italic">
                                    {module.description}
                                  </div>
                                )}
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    )}

                    {/* Available Modules List */}
                    {availableModules.length > 0 && (
                      <div className="mt-3 pt-2 border-t border-gray-200">
                        <div className="font-semibold text-gray-800 mb-2">
                          Available Modules:
                        </div>
                        <div className="space-y-2">
                          {availableModules.map(
                            (module: any, index: number) => (
                              <div key={index} className="text-xs">
                                <div className="font-medium text-gray-700">
                                  {module.name}
                                </div>
                                <div className="text-gray-600 ml-2">
                                  Weight: {module.fitoutWeight?.toFixed(1) || 0}{" "}
                                  tons | Cost:{" "}
                                  {formatCurrency(module.fitoutCost || 0)}
                                </div>
                                {module.description && (
                                  <div className="text-gray-500 ml-2 mt-1 italic">
                                    {module.description}
                                  </div>
                                )}
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
      {/* New row for Weapons section spanning all columns */}
      <div className="mt-6 weapons-section">
        <div className="bg-blue-200 text-blue-900 font-bold px-3 py-1 rounded-t-md text-xs tracking-wide">
          WEAPONS
        </div>
        <table className="w-full border border-blue-200 rounded-b-md text-xs">
          <thead>
            <tr className="bg-blue-50">
              <th className="p-1 border">MOUNT</th>
              <th className="p-1 border">WEAPON</th>
              <th className="p-1 border">TL</th>
              <th className="p-1 border">RANGE</th>
              <th className="p-1 border">DAMAGE</th>
              <th className="p-1 border">AMMUNITION</th>
              <th className="p-1 border">TRAITS</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              if (!weaponMounts || weaponMounts.length === 0) {
                // Show empty rows if no weapons
                return [...Array(3)].map((_, i) => (
                  <tr key={i}>
                    <td className="border p-1 h-6" />
                    <td className="border p-1 h-6" />
                    <td className="border p-1 h-6" />
                    <td className="border p-1 h-6" />
                    <td className="border p-1 h-6" />
                    <td className="border p-1 h-6" />
                    <td className="border p-1 h-6" />
                  </tr>
                ));
              }

              // Group mounts by their type and weapons
              const mountGroups = (weaponMounts || []).reduce(
                (groups, mount) => {
                  if (mount.weapons.length === 0) return groups;

                  // Create a unique key for grouping mounts by type and weapon properties
                  const mountKey = mount.weapons
                    .map((weapon) => {
                      return `${weapon.name}-${weapon.tl}-${weapon.damage}-${weapon.range}-${weapon.tons}-${weapon.cost}-${weapon.power}-${weapon.traits ? [...weapon.traits].sort().join(",") : ""}-${weapon.ammo?.name || "none"}-${weapon.ammo?.tl || "none"}-${weapon.ammo?.traits ? [...weapon.ammo.traits].sort().join(",") : "none"}-${weapon.ammo?.damage || "none"}`;
                    })
                    .sort()
                    .join("|");

                  const fullKey = `${mount.type}${mount.turretType ? `:${mount.turretType}` : ""}:${mountKey}`;
                  if (!groups[fullKey]) groups[fullKey] = [];
                  groups[fullKey].push(mount);
                  return groups;
                },
                {} as Record<string, typeof weaponMounts>,
              );

              // Create mount display name function
              const getMountTypeDisplay = (type: string) => {
                const typeMap: Record<string, string> = {
                  turret: "Turret",
                  barbette: "Barbette",
                  smallBay: "Small Bay",
                  mediumBay: "Medium Bay",
                  largeBay: "Large Bay",
                  spinalMount: "Spinal Mount",
                  pointDefense: "Point Defense",
                  fixed: "Fixed",
                };
                return typeMap[type] || type;
              };

              // Generate rows for each mount group
              const allRows: JSX.Element[] = [];

              Object.entries(mountGroups).forEach(
                ([mountKey, mounts], groupIndex) => {
                  const firstMount = mounts[0];
                  const mountType = getMountTypeDisplay(firstMount.type);

                  // Get weapon names for display
                  const weaponNames = firstMount.weapons
                    .map((w) => w.name)
                    .slice()
                    .sort();
                  let mountDisplayName = `${mountType} (${weaponNames.join(", ")})`;

                  // Add turret type to display name if it's a turret
                  if (firstMount.type === "turret" && firstMount.turretType) {
                    const turretTypeDisplay =
                      firstMount.turretType.charAt(0).toUpperCase() +
                      firstMount.turretType.slice(1);
                    mountDisplayName = `${turretTypeDisplay} ${mountType} (${weaponNames.join(", ")})`;
                  }

                  // Show count if multiple identical mounts
                  const displayMountName =
                    mounts.length > 1
                      ? `${mountDisplayName} (×${mounts.length})`
                      : mountDisplayName;

                  // Group weapons within this mount group
                  const weaponGroups = (firstMount.weapons || []).reduce(
                    (groups, weapon) => {
                      // Create a unique key for grouping weapons by their properties
                      const weaponKey = `${weapon.name}-${weapon.tl}-${weapon.damage}-${weapon.range}-${weapon.tons}-${weapon.cost}-${weapon.power}-${weapon.traits ? [...weapon.traits].sort().join(",") : ""}-${weapon.ammo?.name || "none"}-${weapon.ammo?.tl || "none"}-${weapon.ammo?.traits ? [...weapon.ammo.traits].sort().join(",") : "none"}-${weapon.ammo?.damage || "none"}`;

                      if (!groups[weaponKey]) groups[weaponKey] = [];
                      groups[weaponKey].push(weapon);
                      return groups;
                    },
                    {} as Record<string, typeof firstMount.weapons>,
                  );

                  // Create rows for each weapon group
                  Object.entries(weaponGroups).forEach(
                    ([weaponKey, weapons], weaponGroupIndex) => {
                      const baseWeapon = weapons[0];
                      const isFirstWeapon = weaponGroupIndex === 0;

                      // Calculate total count across all mounts in this group
                      const totalWeaponCount = weapons.length * mounts.length;

                      // Show weapon count if multiple identical weapons
                      const displayWeaponName =
                        weapons.length > 1
                          ? `${baseWeapon.name} (×${weapons.length})`
                          : baseWeapon.name;

                      // Check if this specific weapon type has multiple weapons in a single mount
                      const sameTypeWeapons = firstMount.weapons.filter(
                        (w) => w.name === baseWeapon.name,
                      );
                      let combinedFireDamage = null;
                      if (
                        sameTypeWeapons.length > 1 &&
                        (firstMount.type === "turret" ||
                          firstMount.type === "fixed")
                      ) {
                        const baseWeaponForCalc = sameTypeWeapons[0];
                        const baseDamage = baseWeaponForCalc.damage;
                        const additionalWeapons = sameTypeWeapons.length - 1;
                        const damageMatch = baseDamage.match(/(\d+)D/);
                        if (damageMatch) {
                          const diceCount = parseInt(damageMatch[1]);
                          const bonus = additionalWeapons * diceCount;
                          const damageMultiple =
                            baseWeaponForCalc.damageMultiple || 1;
                          if (damageMultiple > 1) {
                            combinedFireDamage = `(${baseDamage}+${bonus}) × ${damageMultiple} (${sameTypeWeapons.length} weapons)`;
                          } else {
                            combinedFireDamage = `${baseDamage}+${bonus} (${sameTypeWeapons.length} weapons)`;
                          }
                        } else {
                          const damageMultiple =
                            baseWeaponForCalc.damageMultiple || 1;
                          if (damageMultiple > 1) {
                            combinedFireDamage = `${baseDamage} × ${damageMultiple} (${sameTypeWeapons.length} weapons)`;
                          } else {
                            combinedFireDamage = `${baseDamage} (${sameTypeWeapons.length} weapons)`;
                          }
                        }
                      }

                      // If no combined fire found in first mount, check other mounts in the group
                      if (
                        !combinedFireDamage &&
                        (firstMount.type === "turret" ||
                          firstMount.type === "fixed")
                      ) {
                        for (const mount of mounts) {
                          const mountSameTypeWeapons = mount.weapons.filter(
                            (w) => w.name === baseWeapon.name,
                          );
                          if (mountSameTypeWeapons.length > 1) {
                            const baseWeaponForCalc = mountSameTypeWeapons[0];
                            const baseDamage = baseWeaponForCalc.damage;
                            const additionalWeapons =
                              mountSameTypeWeapons.length - 1;
                            const damageMatch = baseDamage.match(/(\d+)D/);
                            if (damageMatch) {
                              const diceCount = parseInt(damageMatch[1]);
                              const bonus = additionalWeapons * diceCount;
                              const damageMultiple =
                                baseWeaponForCalc.damageMultiple || 1;
                              if (damageMultiple > 1) {
                                combinedFireDamage = `(${baseDamage}+${bonus}) × ${damageMultiple} (${mountSameTypeWeapons.length} weapons)`;
                              } else {
                                combinedFireDamage = `${baseDamage}+${bonus} (${mountSameTypeWeapons.length} weapons)`;
                              }
                            } else {
                              const damageMultiple =
                                baseWeaponForCalc.damageMultiple || 1;
                              if (damageMultiple > 1) {
                                combinedFireDamage = `${baseDamage} × ${damageMultiple} (${mountSameTypeWeapons.length} weapons)`;
                              } else {
                                combinedFireDamage = `${baseDamage} (${mountSameTypeWeapons.length} weapons)`;
                              }
                            }
                            break; // Found one, that's enough
                          }
                        }
                      }

                      allRows.push(
                        <tr
                          key={`${groupIndex}-${baseWeapon.name}-${weaponGroupIndex}`}
                        >
                          <td className="border p-1 h-6 text-xs">
                            {isFirstWeapon ? displayMountName : ""}
                          </td>
                          <td className="border p-1 h-6 text-xs">
                            {displayWeaponName}
                          </td>
                          <td className="border p-1 h-6 text-xs text-center">
                            {baseWeapon.tl || "—"}
                          </td>
                          <td className="border p-1 h-6 text-xs text-center">
                            {baseWeapon.range || "—"}
                          </td>
                          <td className="border p-1 h-6 text-xs text-center">
                            <div>
                              <div>{baseWeapon.damage}</div>
                              {combinedFireDamage && (
                                <div className="text-xs text-blue-600 font-semibold">
                                  Combined: {combinedFireDamage}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="border p-1 h-6 text-xs text-center">
                            {baseWeapon.ammo ? (
                              <div>
                                <div>
                                  {baseWeapon.ammo.name || baseWeapon.ammo.type} ×{baseWeapon.ammo.capacity || 0}
                                </div>
                                {totalWeaponCount > 1 && (
                                  <div className="text-xs text-gray-500">
                                    ×{totalWeaponCount} mounts
                                  </div>
                                )}
                                {baseWeapon.extraReloads &&
                                  baseWeapon.extraReloads > 0 && (
                                    <div className="text-xs text-gray-500">
                                      +{baseWeapon.extraReloads} reloads
                                    </div>
                                  )}
                              </div>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="border p-1 h-6 text-xs text-center">
                            {baseWeapon.traits &&
                            Array.isArray(baseWeapon.traits) &&
                            baseWeapon.traits.length > 0
                              ? baseWeapon.traits.join(", ")
                              : "—"}
                          </td>
                        </tr>,
                      );
                    },
                  );
                },
              );

              return allRows;
            })()}
            {/* Add additional empty rows to maintain minimum table height if needed */}
            {(() => {
              const totalWeaponRows = weaponMounts
                ? weaponMounts.reduce((total, mount) => {
                    return total + mount.weapons.length;
                  }, 0)
                : 0;

              const neededRows = Math.max(0, 3 - totalWeaponRows);
              return [...Array(neededRows)].map((_, i) => (
                <tr key={`empty-${i}`}>
                  <td className="border p-1 h-6" />
                  <td className="border p-1 h-6" />
                  <td className="border p-1 h-6" />
                  <td className="border p-1 h-6" />
                  <td className="border p-1 h-6" />
                  <td className="border p-1 h-6" />
                  <td className="border p-1 h-6" />
                </tr>
              ));
            })()}
          </tbody>
        </table>
      </div>
      {/* New row for Staterooms and Passengers section and Cargo Hold Content side by side */}
      <div className="mt-6">
        <div className="grid grid-cols-2 gap-6">
          {/* Staterooms and Passengers */}
          <div className="staterooms-section">
            <div className="bg-blue-200 text-blue-900 font-bold px-3 py-1 rounded-t-md text-xs tracking-wide">
              STATEROOMS AND PASSENGERS
            </div>
            <div className="border border-t-0 border-blue-200 rounded-b-md p-3 text-xs">
              <div className="grid grid-cols-2 gap-4">
                {/* Staterooms */}
                <div>
                  <div className="font-semibold text-gray-800 mb-2">
                    Staterooms
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span>Standard Staterooms:</span>
                      <span className="font-semibold">
                        {stateroomConfig?.single || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Double Staterooms:</span>
                      <span className="font-semibold">
                        {stateroomConfig?.double || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Low Berths:</span>
                      <span className="font-semibold">
                        {stateroomConfig?.lowBerths?.count || 0}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Passengers */}
                <div>
                  <div className="font-semibold text-gray-800 mb-2">
                    Passengers
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span>High Passengers:</span>
                      <span className="font-semibold">
                        {passengerConfig?.high || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Middle Passengers:</span>
                      <span className="font-semibold">
                        {passengerConfig?.middle || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Basic Passengers:</span>
                      <span className="font-semibold">
                        {passengerConfig?.basic || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Low Passengers:</span>
                      <span className="font-semibold">
                        {passengerConfig?.low || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Total Passengers:</span>
                      <span className="font-semibold">
                        {(passengerConfig?.high || 0) +
                          (passengerConfig?.middle || 0) +
                          (passengerConfig?.basic || 0) +
                          (passengerConfig?.low || 0)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Cargo Hold Content */}
          <div className="cargo-section">
            <div className="bg-blue-200 text-blue-900 font-bold px-3 py-1 rounded-t-md text-xs tracking-wide">
              CARGO HOLD CONTENT
            </div>
            <div className="border border-t-0 border-blue-200 rounded-b-md p-2">
              <table className="w-full text-xs">
                <thead>
                  <tr>
                    <th className="text-left font-semibold">ITEM</th>
                    <th className="text-right font-semibold">TONS</th>
                  </tr>
                </thead>
                <tbody>
                  {supplyTons > 0 && (
                    <tr>
                      <td>Supplies ({supplyUnits} SU)</td>
                      <td className="text-right">{(supplyTons ?? 0).toFixed(2)}</td>
                    </tr>
                  )}
                  {ammunitionStorage.length > 0 && ammunitionStorage.map((ammo) => (
                    <tr key={ammo.id}>
                      <td>{ammo.name} x{ammo.quantity}</td>
                      <td className="text-right">{(ammo.tonsPerReload * ammo.quantity).toFixed(2)}</td>
                    </tr>
                  ))}
                  {/* Fallback empty rows if nothing present */}
                  {supplyTons <= 0 && ammunitionStorage.length === 0 &&
                    [...Array(5)].map((_, i) => (
                      <tr key={i}>
                        <td className="h-4 border-b border-dashed border-gray-300" />
                        <td className="h-4 border-b border-dashed border-gray-300" />
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      {/* New row for Systems section spanning all columns */}
      <div className="mt-6 systems-section">
        <div className="bg-blue-200 text-blue-900 font-bold px-3 py-1 rounded-t-md text-xs tracking-wide">
          SYSTEMS
        </div>
        <div className="border border-t-0 border-blue-200 rounded-b-md p-3 text-xs">
          {(() => {
            // Get all installed optional systems (excluding modular hull)
            const allOptionalSystems = [
              ...(Array.isArray(optionalSystems.structure)
                ? optionalSystems.structure
                : []),
              ...(Array.isArray(optionalSystems.power)
                ? optionalSystems.power
                : []),
              ...(Array.isArray(optionalSystems.drives)
                ? optionalSystems.drives
                : []),
              ...(Array.isArray(optionalSystems.fuel)
                ? optionalSystems.fuel
                : []),
              ...(Array.isArray(optionalSystems.accommodations)
                ? optionalSystems.accommodations
                : []),
              ...(Array.isArray(optionalSystems.bridge)
                ? optionalSystems.bridge
                : []),
              ...(Array.isArray(optionalSystems.cargo)
                ? optionalSystems.cargo
                : []),
              ...(Array.isArray(optionalSystems.drones)
                ? optionalSystems.drones
                : []),
              ...(Array.isArray(optionalSystems.sensors)
                ? optionalSystems.sensors
                : []),
              ...(Array.isArray(optionalSystems.external)
                ? optionalSystems.external
                : []),
              ...(Array.isArray(optionalSystems.internal)
                ? optionalSystems.internal
                : []),
              ...(Array.isArray(optionalSystems.software)
                ? optionalSystems.software
                : []),
              ...(Array.isArray(optionalSystems.commonAreas)
                ? optionalSystems.commonAreas
                : []),
            ];

            if (allOptionalSystems.length === 0) {
              return (
                <div className="text-xs text-gray-500 italic">
                  No optional systems installed
                </div>
              );
            }

            // Helper function to get system details (matching Optional Systems Summary logic)
            const getSystemDetails = (system: any) => {
              // Match the exact logic from Optional Systems Summary
              if (
                system.subtype === "gourmetKitchen" &&
                system.properties.diners
              ) {
                return `${system.properties.diners} diners`;
              }
              if (system.subtype === "hotTub" && system.properties.travellers) {
                return `${system.properties.travellers} travellers`;
              }
              if (
                system.subtype === "swimmingPool" ||
                system.subtype === "endlessPool"
              ) {
                const poolType = system.properties.poolType || "";
                const travellers = system.properties.travellers
                  ? `${system.properties.travellers} travellers`
                  : "";
                return `${poolType} ${travellers}`.trim();
              }
              if (system.subtype === "zeroGRoom") {
                return `${system.tonnage} tons`;
              }
              if (system.subtype === "advancedEntertainment") {
                return `TL${system.techLevel}`;
              }
              // Laboratory, Studio, Training Facilities details
              if (
                system.subtype === "internalLaboratory" &&
                system.properties.scientists
              ) {
                return `${system.properties.scientists} scientists`;
              }
              if (
                system.subtype === "internalStudio" &&
                system.properties.people
              ) {
                return `${system.properties.people} users`;
              }
              if (
                system.subtype === "internalTrainingFacilities" &&
                system.properties.people
              ) {
                return `${system.properties.people} trainees`;
              }
              // Bridge system details
              if (system.type === "bridge") {
                switch (system.subtype) {
                  case "holographic":
                  case "sensorStation":
                    if (system.properties?.quantity) {
                      return `${system.properties.quantity}`;
                    }
                    return "1";
                  case "detachable":
                    return null; // No details shown in Step 10
                  default:
                    return null;
                }
              }

              // Accommodation system details
              if (system.type === "accommodation") {
                switch (system.subtype) {
                  case "accelerationBench":
                  case "accelerationSeat":
                    if (system.properties?.seats) {
                      const seats = Number(system.properties.seats) || 0;
                      const quantity = Number(system.properties.quantity) || 1;
                      const totalSeats = seats * quantity;
                      return `${totalSeats} passengers`;
                    }
                    return null;
                  case "barracks":
                    if (system.properties?.quantity) {
                      const people = Number(system.properties.quantity) || 1;
                      const designation =
                        system.properties.designation || "passenger";
                      const designationText =
                        designation === "crew" ? "Crew" : "Basic Passage";
                      return `${people} ${designationText}`;
                    }
                    return null;
                  case "brig":
                    if (system.properties) {
                      const comfortablePrisoners =
                        Number(system.properties.prisoners) || 6;
                      const maxPrisoners =
                        Number(system.properties.maxPrisoners) || 12;
                      const quantity = Number(system.properties.quantity) || 1;
                      const totalComfortable = comfortablePrisoners * quantity;
                      const totalMax = maxPrisoners * quantity;
                      return `${totalComfortable} prisoners (up to ${totalMax} uncomfortably)`;
                    }
                    return null;
                  case "cabinSpace":
                    if (system.properties?.quantity) {
                      const passengers =
                        Number(system.properties.quantity) || 1;
                      return `${passengers} passengers`;
                    }
                    return null;
                  case "highStateroom":
                  case "luxuryStateroom":
                    if (system.properties?.quantity) {
                      return `${system.properties.quantity}`;
                    }
                    return null;
                  case "multiEnvironmentSpace":
                    if ((system as AccommodationSystem).coveredSpaces) {
                      const coveredSpaces = (system as AccommodationSystem)
                        .coveredSpaces!;
                      const customArea = coveredSpaces.find(
                        (space: {
                          id: string;
                          label: string;
                          tons: number;
                          environmentType: string;
                        }) => space.id === "custom",
                      );
                      if (customArea) {
                        return `${customArea.tons} tons, ${customArea.environmentType}`;
                      }
                      const areas = coveredSpaces.map(
                        (space: {
                          id: string;
                          label: string;
                          tons: number;
                          environmentType: string;
                        }) => `${space.label} (${space.tons} tons)`,
                      );
                      return areas.join(", ");
                    }
                    return null;
                  default:
                    return null;
                }
              }

              // UNREP system transfer rate details
              if (
                system.subtype === "internalUnrepSystem" &&
                typeof system.tonnage === "number" &&
                typeof system.properties?.transferPerTon === "number"
              ) {
                const transferRate =
                  system.tonnage * system.properties.transferPerTon;
                return `${transferRate} tons/hour`;
              }
              // Vault armor and hits details
              if (
                system.subtype === "internalVault" &&
                typeof system.tonnage === "number"
              ) {
                const armor = Math.min(system.tonnage, 10); // 1 per ton, max 10
                const hits = Math.ceil(system.tonnage / 5); // 1 per 5 tons
                return `${armor} armor, ${hits} hits`;
              }
              // Capsule type and quantity details
              if (
                [
                  "internalReEntryCapsule",
                  "internalAssaultCapsule",
                  "internalHighSurvivabilityCapsule",
                  "internalReEntryPod",
                ].includes(system.subtype)
              ) {
                // For grouped capsules, use the count from grouped systems
                if (isGroupedSystem(system)) {
                  const capsuleCount = system._groupedSystems.length;
                  const capsuleType = system.name;
                  return `${capsuleType} (${capsuleCount})`;
                } else {
                  // For individual capsules (fallback)
                  const capsuleCount = allOptionalSystems.filter(
                    (s) => s.subtype === system.subtype,
                  ).length;
                  const capsuleType = system.name;
                  return `${capsuleType} (${capsuleCount})`;
                }
              }
              // Armoury crew/marine capacity details
              if (
                system.subtype === "internalArmoury" &&
                typeof system.tonnage === "number"
              ) {
                const crewCapacity = system.tonnage * 25; // 25 crew per ton
                const marineCapacity = system.tonnage * 5; // 5 marines per ton
                return `${crewCapacity} crew or ${marineCapacity} marines`;
              }
              // Biosphere passenger capacity details
              if (
                system.subtype === "internalBiosphere" &&
                typeof system.tonnage === "number"
              ) {
                const passengerCapacity = system.tonnage * 2; // 2 passengers per ton
                return `${passengerCapacity} passengers`;
              }

              // Additional details for other system types
              if (
                system.type === "modularHull" &&
                Array.isArray(system.modules)
              ) {
                const installedModules = system.modules.filter(
                  (m: any) => m.installed,
                );
                if (installedModules.length > 0) {
                  return `${installedModules.length}`;
                }
              }

              if (
                system.type === "structure" &&
                system.subtype === "armouredBulkhead"
              ) {
                if (
                  system.protectedSystems &&
                  Array.isArray(system.protectedSystems)
                ) {
                  const protectedSystems = system.protectedSystems;
                  const formattedNames = protectedSystems.map(
                    (systemName: string) => {
                      // Convert system names to proper format
                      const nameMap: { [key: string]: string } = {
                        staterooms: "Staterooms",
                        reactionDrive: "Reaction Drive",
                        maneuverDrive: "Maneuver Drive",
                        jumpDrive: "Jump Drive",
                        powerPlant: "Power Plant",
                        bridge: "Bridge",
                        sensors: "Sensors",
                        weapons: "Weapons",
                        cargo: "Cargo",
                        fuel: "Fuel",
                        passengers: "Passengers",
                        crew: "Crew",
                      };
                      return nameMap[systemName] || systemName;
                    },
                  );
                  return formattedNames.join(", ");
                }
                return null;
              }

              // Fuel system details
              if (system.type === "fuel") {
                switch (system.subtype) {
                  case "collapsibleTank":
                    if (typeof system.tonnage === "number") {
                      return `${system.tonnage} Tons`;
                    }
                    return null;
                  case "dropTank":
                    if (typeof system.tonnage === "number") {
                      return `${system.tonnage} Tons`;
                    }
                    return null;
                  case "fuelCargoContainer":
                    const config =
                      (system.properties?.allocation as string) || "fuel";
                    const capacity = system.properties?.capacity || 0;
                    return `${capacity} Tons, ${config.charAt(0).toUpperCase() + config.slice(1)}`;
                  case "fuelProcessor":
                    if (system.properties?.processingRate) {
                      return `${system.properties.processingRate} tons/day`;
                    }
                    return null;
                  case "fuelTankCompartment":
                    const percentage =
                      (system.properties as any)?.percentage || 0;
                    const fuelCapacity = (fuelTanks * percentage) / 100;
                    return `${fuelCapacity.toFixed(1)} Tons`;
                  case "mountableTank":
                    if (typeof system.tonnage === "number") {
                      return `${system.tonnage} Tons`;
                    }
                    return null;
                  case "ramscoop":
                    if (typeof system.tonnage === "number") {
                      const collectionRate =
                        (system.properties as any)?.collectionRate ||
                        system.tonnage * 5;
                      return `${collectionRate} Tons/week`;
                    }
                    return null;
                  default:
                    return null;
                }
              }

              // Sensor system details
              if (system.type === "sensor") {
                switch (system.subtype) {
                  case "deepPenetrationScanners":
                    if (typeof system.tonnage === "number") {
                      const scanRate = system.tonnage * 20; // 20 tons/hour per 1 ton of scanners
                      return `${scanRate} Tons/hour`;
                    }
                    return null;
                  case "countermeasuresSuite":
                  case "militaryCountermeasuresSuite":
                    if (system.properties?.jamDM) {
                      return `DM+${system.properties.jamDM}`;
                    }
                    return null;
                  case "improvedSignalProcessing":
                  case "enhancedSignalProcessing":
                    if (system.properties?.sensorDM) {
                      return `DM+${system.properties.sensorDM}`;
                    }
                    return null;
                  default:
                    return null;
                }
              }

              // Docking Clamp type details
              if (
                system.type === "external" &&
                system.subtype === "dockingClamp" &&
                system.properties?.clampType
              ) {
                const clampType = system.properties.clampType as string;
                const typeMap: { [key: string]: string } = {
                  typeI: "Type I",
                  typeII: "Type II",
                  typeIII: "Type III",
                  typeIV: "Type IV",
                  typeV: "Type V",
                };
                const typeLabel = typeMap[clampType] || "Unknown Type";
                const maxTonnage = system.properties.maxAttachedTonnage || 0;
                return `${typeLabel} (up to ${maxTonnage} tons)`;
              }

              // Forced Linkage Apparatus type details
              if (
                system.type === "external" &&
                system.subtype === "forcedLinkage" &&
                system.properties?.linkageType
              ) {
                const linkageType = system.properties.linkageType as string;
                const typeMap: { [key: string]: string } = {
                  basic: "Basic (TL7)",
                  improved: "Improved (TL9)",
                  enhanced: "Enhanced (TL12)",
                  advanced: "Advanced (TL15)",
                };
                const typeLabel = typeMap[linkageType] || "Unknown Type";
                const pilotDM = system.properties.pilotDM || 0;
                const dmText = pilotDM >= 0 ? `+${pilotDM}` : `${pilotDM}`;
                return `${typeLabel}, Pilot DM ${dmText}`;
              }

              // Grappling Arm type details
              if (
                system.type === "external" &&
                system.subtype === "grapplingArm" &&
                system.properties?.armType
              ) {
                const armType = system.properties.armType as string;
                const typeMap: { [key: string]: string } = {
                  standard: "Standard",
                  heavy: "Heavy",
                };
                const typeLabel = typeMap[armType] || "Unknown Type";
                const maxWeight = system.properties.maxWeight || 0;
                return `${typeLabel} (up to ${maxWeight} tons)`;
              }

              // Booby Trap Airlock details
              if (
                system.type === "internal" &&
                system.subtype === "internalBoobyTrap" &&
                system.properties?.boobyTrapType
              ) {
                // For grouped booby trap airlocks, show total details
                if (
                  (system as any)._quantity &&
                  (system as any)._groupedSystems
                ) {
                  const groupedSystems = (system as any)._groupedSystems;
                  // Since all systems in a group have the same configuration, we can use the first one
                  const firstSystem = groupedSystems[0];
                  const trappedCount =
                    firstSystem.properties?.trappedCount || 1;
                  const totalTrappedCount =
                    trappedCount * (system as any)._quantity;
                  const trapType = system.properties.boobyTrapType as string;
                  const typeMap: { [key: string]: string } = {
                    basic: "Basic",
                    improved: "Improved",
                    enhanced: "Enhanced",
                    advanced: "Advanced",
                  };
                  const typeLabel = typeMap[trapType] || "Unknown Type";
                  return `${typeLabel} Trap, ${totalTrappedCount} airlock${totalTrappedCount > 1 ? "s" : ""}`;
                } else {
                  // For individual booby trap airlocks
                  const trapType = system.properties.boobyTrapType as string;
                  const typeMap: { [key: string]: string } = {
                    basic: "Basic",
                    improved: "Improved",
                    enhanced: "Enhanced",
                    advanced: "Advanced",
                  };
                  const typeLabel = typeMap[trapType] || "Unknown Type";
                  const trappedCount = system.properties.trappedCount || 1;
                  return `${typeLabel} Trap, ${trappedCount} airlock${trappedCount > 1 ? "s" : ""}`;
                }
              }

              // Medical Bay patient capacity details
              if (
                system.type === "internal" &&
                system.subtype === "internalMedicalBay"
              ) {
                // For grouped medical bays, show total patient capacity
                if (
                  (system as any)._quantity &&
                  (system as any)._groupedSystems
                ) {
                  const totalPatients = (system as any)._quantity * 3; // 3 patients per medical bay
                  return `${totalPatients} patients`;
                } else {
                  // For individual medical bays
                  return `3 patients`;
                }
              }

              // Concealed Compartment contraband capacity details
              if (
                system.type === "internal" &&
                system.subtype === "internalConcealedCompartment" &&
                typeof system.tonnage === "number"
              ) {
                return `${system.tonnage} tons`;
              }

              // Construction Deck build capacity details
              if (
                system.type === "internal" &&
                system.subtype === "internalConstructionDeck" &&
                typeof system.tonnage === "number"
              ) {
                // Fix floating-point precision issue: use integer math to avoid calculation errors
                const buildCapacity = Math.floor((system.tonnage * 5) / 10); // Can build up to half the deck's tonnage
                return `${buildCapacity} tons`;
              }

              // Docking Space maximum vessel size details
              if (
                system.type === "internal" &&
                system.subtype === "internalDockingSpace" &&
                typeof system.tonnage === "number"
              ) {
                // Docking space tonnage = largest ship + 10%, so max vessel size = tonnage / 1.1
                // Fix floating-point precision issue: use integer math to avoid division errors
        const maxVesselSize = Math.floor((system.tonnage * 10) / 11);
                return `${maxVesselSize} tons`;
              }

              // Full Hangar craft capacity details
              if (
                system.type === "internal" &&
                system.subtype === "internalFullHangar" &&
                typeof system.tonnage === "number"
              ) {
                // Full hangar tonnage = 2 × craft tonnage, so craft capacity = tonnage / 2
                const craftCapacity = Math.floor(system.tonnage / 2);
                return `${craftCapacity} tons`;
              }

              // Grav Screen auto-calculated details
              if (
                system.type === "internal" &&
                system.subtype === "internalGravScreen" &&
                typeof system.tonnage === "number"
              ) {
                // Grav screen tonnage = 1 ton per 200 tons of hull
                return `${hullSize} ton hull`;
              }

              // Launch Tube size maximum details
              if (
                system.type === "internal" &&
                system.subtype === "internalLaunchTube" &&
                system.properties?.launchTubeMaxSize
              ) {
                return `${system.properties.launchTubeMaxSize} tons`;
              }

              // Recovery Deck size maximum details
              if (
                system.type === "internal" &&
                system.subtype === "internalRecoveryDeck" &&
                system.properties?.recoveryDeckMaxSize
              ) {
                return `${system.properties.recoveryDeckMaxSize} tons`;
              }

              // Stable creature capacity details
              if (
                system.type === "internal" &&
                system.subtype === "internalStable" &&
                typeof system.tonnage === "number"
              ) {
                // For grouped stables, show total capacity and quantity
                if (isGroupedSystem(system)) {
                  const quantity = system._groupedSystems.length;
                  const humanCapacity = Math.floor((system.tonnage / 10) * 20);
                  const cattleCapacity = Math.floor((system.tonnage / 10) * 10);
                  return `${cattleCapacity} cattle-size or ${humanCapacity} human-size (${quantity} installed)`;
                } else {
                  // For individual stables
                  const humanCapacity = Math.floor((system.tonnage / 10) * 20);
                  const cattleCapacity = Math.floor((system.tonnage / 10) * 10);
                  return `${cattleCapacity} cattle-size or ${humanCapacity} human-size`;
                }
              }

              // Workshop mechanics capacity details
              if (
                system.type === "internal" &&
                system.subtype === "internalWorkshop" &&
                typeof system.tonnage === "number"
              ) {
                // For grouped workshops, show total capacity and quantity
                if (isGroupedSystem(system)) {
                  const quantity = system._groupedSystems.length;
                  const mechanicsCapacity = Math.floor(system.tonnage / 3); // 3 tons per mechanic (6 tons / 2 mechanics)
                  return `${mechanicsCapacity} mechanics (${quantity} installed)`;
                } else {
                  // For individual workshops
                  const mechanicsCapacity = Math.floor(system.tonnage / 3); // 3 tons per mechanic (6 tons / 2 mechanics)
                  return `${mechanicsCapacity} mechanics`;
                }
              }

              if (system.type === "software" && system.properties?.level) {
                return `${system.properties.level}`;
              }

              // Power system details - match exactly what Step 10 shows
              if (system.type === "power") {
                switch (system.subtype) {
                  case "auxiliary":
                    return null; // No details shown in Step 10
                  case "emergency":
                    return null; // No details shown in Step 10
                  case "battery":
                    // Match Step 10: "Storage Capacity: X.X Power"
                    const storageCapacity = system.power || 0;
                    return `${storageCapacity.toFixed(1)} Power`;
                  case "solarPanels":
                    // Match Step 10: "Generates X.X Power when deployed"
                    const powerPerUnit =
                      (system.properties as any)?.powerPerUnit?.[
                        system.techLevel
                      ] || 1;
                    const deploymentPower = system.tonnage * powerPerUnit;
                    return `${deploymentPower.toFixed(1)} Power`;
                  default:
                    return null;
                }
              }

              // Cargo system details
              if (system.type === "cargo") {
                switch (system.subtype) {
                  case "cargoCrane":
                    // For grouped cargo cranes, show total capacity
                    if (isGroupedSystem(system)) {
                      const quantity = system._groupedSystems.length;
                      const capacity = 1000 * quantity; // 1000 tons per crane
                      return `${capacity} tons (${quantity} installed)`;
                    } else {
                      return "1,000 tons";
                    }
                  case "cargoNet":
                    return null; // No specific details
                  case "cargoScoop":
                    return null; // No specific details
                  case "externalCargoMount":
                    return "100 tons";
                  case "jumpNet":
                    // Check if it's interplanetary or interstellar based on properties
                    if (system.properties?.jumpType === "interplanetary") {
                      return "Interplanetary, 100 tons";
                    } else {
                      return "Interstellar, 400 tons";
                    }
                  case "loadingBelt7":
                  case "loadingBelt12":
                    // For grouped loading belts, show total crew replacement
                    if (isGroupedSystem(system)) {
                      const quantity = system._groupedSystems.length;
                      const crewReplacement =
                        system._totalCrewReplacement || 25 * quantity;
                      return `${crewReplacement} crewmen (${quantity} installed)`;
                    } else {
                      const crewReplacement =
                        system.properties?.crewReplacement || 25;
                      return `${crewReplacement} crewmen`;
                    }
                  default:
                    return null;
                }
              }

              // Drive system details
              if (system.type === "drive") {
                switch (system.subtype) {
                  case "highBurn":
                    if (system.properties?.selectedThrust) {
                      return `${system.properties.selectedThrust}`;
                    }
                    return null;
                  case "concealed":
                    const maneuverDrive = drives.find(
                      (d) => d.type === "maneuver",
                    );
                    if (maneuverDrive) {
                      const reducedThrust = Math.floor(
                        maneuverDrive.rating * 0.5,
                      );
                      return `M-Drive Thrust ${reducedThrust}`;
                    }
                    return "M-Drive Thrust -50%";
                  case "solarSail":
                    return null; // No details shown in Step 10
                  default:
                    return null;
                }
              }

              // External system details
              if (system.type === "external") {
                switch (system.subtype) {
                  case "dockingClamp":
                    if (system.properties?.clampType) {
                      const clampType = system.properties.clampType as string;
                      const typeMap: { [key: string]: string } = {
                        typeI: "Type I",
                        typeII: "Type II",
                        typeIII: "Type III",
                        typeIV: "Type IV",
                        typeV: "Type V",
                      };
                      const typeLabel = typeMap[clampType] || "Unknown Type";
                      const maxTonnage =
                        system.properties.maxAttachedTonnage || 0;
                      return `${typeLabel} (${maxTonnage} tons)`;
                    }
                    return null;
                  case "forcedLinkage":
                    if (system.properties?.linkageType) {
                      const linkageType = system.properties
                        .linkageType as string;
                      const typeMap: { [key: string]: string } = {
                        basic: "Basic (TL7)",
                        improved: "Improved (TL9)",
                        enhanced: "Enhanced (TL12)",
                        advanced: "Advanced (TL15)",
                      };
                      const typeLabel = typeMap[linkageType] || "Unknown Type";
                      const pilotDM = system.properties.pilotDM || 0;
                      const dmText =
                        pilotDM >= 0 ? `+${pilotDM}` : `${pilotDM}`;
                      return `${typeLabel}, DM ${dmText}`;
                    }
                    return null;
                  default:
                    return null;
                }
              }

              // Drone system details
              if (system.type === "drone") {
                switch (system.subtype) {
                  case "mining":
                  case "probe":
                  case "advancedProbe":
                  case "repair":
                    // For grouped drones, show quantity
                    if (isGroupedSystem(system)) {
                      const quantity = system._groupedSystems.length;
                      return `${quantity}`;
                    } else {
                      // For individual drones, show quantity 1
                      return "1";
                    }
                  default:
                    return null;
                }
              }

              return null;
            };

            // Group capsules and stables for display
            const displaySystems = groupSystems(allOptionalSystems);

            // Group systems by type for sub-headings (excluding sensors since they have their own section)
            const groupedSystems = displaySystems.reduce(
              (groups: Record<string, any[]>, system) => {
                const type = system.type || "other";
                // Skip sensor systems as they have their own dedicated section
                if (type === "sensor") {
                  return groups;
                }
                if (!groups[type]) {
                  groups[type] = [];
                }
                groups[type].push(system);
                return groups;
              },
              {},
            );

            // Define display names for system types
            const getTypeDisplayName = (type: string) => {
              switch (type) {
                case "structure":
                  return "STRUCTURAL";
                case "power":
                  return "POWER SYSTEMS";
                case "drives":
                  return "DRIVE SYSTEMS";
                case "fuel":
                  return "FUEL SYSTEMS";
                case "accommodations":
                  return "ACCOMMODATIONS";
                case "bridge":
                  return "BRIDGE SYSTEMS";
                case "cargo":
                  return "CARGO SYSTEMS";
                case "internal":
                  return "INTERNAL SYSTEMS";
                case "external":
                  return "EXTERNAL SYSTEMS";
                case "sensor":
                  return "SENSOR SYSTEMS";
                case "software":
                  return "SOFTWARE SYSTEMS";
                case "weapon":
                  return "WEAPON SYSTEMS";
                case "defensive":
                  return "DEFENSIVE SYSTEMS";
                case "commonArea":
                  return "COMMON AREAS";
                default:
                  return type.toUpperCase();
              }
            };

            // Define colors for system types
            const getTypeColor = (type: string) => {
              switch (type) {
                case "structure":
                  return "bg-gray-200 text-gray-900";
                case "power":
                  return "bg-yellow-200 text-yellow-900";
                case "drives":
                  return "bg-blue-200 text-blue-900";
                case "fuel":
                  return "bg-orange-200 text-orange-900";
                case "accommodations":
                  return "bg-purple-200 text-purple-900";
                case "bridge":
                  return "bg-indigo-200 text-indigo-900";
                case "cargo":
                  return "bg-green-200 text-green-900";
                case "internal":
                  return "bg-pink-200 text-pink-900";
                case "external":
                  return "bg-teal-200 text-teal-900";
                case "sensor":
                  return "bg-cyan-200 text-cyan-900";
                case "software":
                  return "bg-violet-200 text-violet-900";
                case "weapon":
                  return "bg-red-200 text-red-900";
                case "defensive":
                  return "bg-amber-200 text-amber-900";
                default:
                  return "bg-gray-200 text-gray-900";
              }
            };

            const groupedEntries = Object.entries(groupedSystems) as [
              string,
              any[],
            ][];

            // Function to create a system box with max height and column wrapping
            const createSystemBox = (type: string, systems: any[]) => {
              const maxItemsPerColumn = 10; // Max 10 items before wrapping to next column
              const columns = [];

              for (let i = 0; i < systems.length; i += maxItemsPerColumn) {
                const columnSystems = systems.slice(i, i + maxItemsPerColumn);
                columns.push(columnSystems);
              }

              return (
                <div
                  key={type}
                  className="text-xs border border-gray-300 rounded-md p-2 bg-white flex-shrink-0 max-w-2xl min-w-fit"
                >
                  <div
                    className={`${getTypeColor(type)} font-bold px-2 py-1 rounded text-xs tracking-wide mb-2`}
                  >
                    {getTypeDisplayName(type)}
                  </div>
                  <div
                    className="grid gap-2"
                    style={{
                      gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))`,
                    }}
                  >
                    {columns.map((columnSystems, colIndex) => (
                      <div key={colIndex} className="min-w-0">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b border-gray-300">
                              <th className="text-left py-1 font-medium text-gray-700 text-xs border-r border-gray-300 pr-2">
                                System Name
                              </th>
                              <th className="text-left py-1 font-medium text-gray-700 text-xs pl-2">
                                Details
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {columnSystems.map((system: any, index: number) => {
                              const details = getSystemDetails(system);
                              return (
                                <tr
                                  key={index}
                                  className="border-b border-gray-200 text-xs"
                                >
                                  <td className="py-1 font-medium text-gray-800 text-xs border-r border-gray-300 pr-2">
                                    {system.name}
                                  </td>
                                  <td className="py-1 text-gray-600 text-xs pl-2">
                                    {details || "—"}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                </div>
              );
            };

            // Arrange system boxes in a flex layout that allows natural wrapping
            const systemBoxes = groupedEntries.map(([type, systems]) =>
              createSystemBox(type, systems),
            );

            return (
              <div className="flex flex-wrap gap-4 justify-start">
                {systemBoxes}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Small Craft Section */}
      <div className="mt-6">
        <div className="bg-green-200 text-green-900 font-bold px-3 py-1 rounded-t-md text-xs tracking-wide">
          SMALL CRAFT
        </div>
        <div className="border border-t-0 border-green-200 rounded-b-md p-3 text-xs overflow-x-auto">
          {smallCraft.length === 0 ? (
            <div className="text-xs text-gray-500 italic">No small craft assigned</div>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-green-300 bg-green-50">
                  <th className="text-left py-1 px-2 font-medium text-green-900">Type</th>
                  <th className="text-left py-1 px-2 font-medium text-green-900">Size (tons)</th>
                  <th className="text-left py-1 px-2 font-medium text-green-900">Quantity</th>
                  <th className="text-left py-1 px-2 font-medium text-green-900">Docking Location</th>
                  <th className="text-left py-1 px-2 font-medium text-green-900">Assigned System</th>
                  <th className="text-left py-1 px-2 font-medium text-green-900">Details</th>
                </tr>
              </thead>
              <tbody>
                {smallCraft
                  .slice() // Create a shallow copy to avoid mutating the original state array
                  .sort((a, b) => {
                    // Custom sort logic for assignedSystem
                    const getSortKey = (systemId: string) => {
                      const parts = systemId.split('-');
                      const type = parts[0]; // e.g., 'clamp', 'space', 'hangar'
                      const index = parseInt(parts[1], 10); // e.g., 0, 1, 2

                      // Define a custom order for types: Clamp -> Space -> Hangar
                      const typeOrder: { [key: string]: number } = {
                        'clamp': 1,
                        'space': 2,
                        'hangar': 3,
                      };

                      // Create a sortable string: e.g., "1-000", "2-001"
                      return `${typeOrder[type] || 99}-${index.toString().padStart(3, '0')}`;
                    };

                    const keyA = getSortKey(a.assignedSystem);
                    const keyB = getSortKey(b.assignedSystem);

                    if (keyA < keyB) return -1;
                    if (keyA > keyB) return 1;
                    return 0;
                  })
                  .map((craft) => (
                  <tr key={craft.id} className="border-b border-green-100">
                    <td className="py-1 px-2">{craft.type}</td>
                    <td className="py-1 px-2">{craft.size}</td>
                    <td className="py-1 px-2">{craft.quantity}</td>
                    <td className="py-1 px-2 capitalize">{craft.dockingLocation}</td>
                    <td className="py-1 px-2">{(() => {
                      // Convert system ID to user-friendly name
                      const systemId = craft.assignedSystem;
                      if (systemId.startsWith('clamp-')) {
                        const index = parseInt(systemId.replace('clamp-', ''));
                        return `Docking Clamp ${index + 1}`;
                      } else if (systemId.startsWith('space-')) {
                        const index = parseInt(systemId.replace('space-', ''));
                        return `Docking Space ${index + 1}`;
                      } else if (systemId.startsWith('hangar-')) {
                        const index = parseInt(systemId.replace('hangar-', ''));
                        return `Full Hangar ${index + 1}`;
                      }
                      return systemId; // Fallback to original if pattern doesn't match
                    })()}</td>
                    <td className="py-1 px-2">{craft.details}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* New row for Critical Hits section spanning all columns at the bottom */}
      <div className="mt-6 critical-hits-section">
        <div className="bg-orange-400 text-white font-bold px-3 py-1 rounded-t-md text-xs tracking-wide">
          CRITICAL HITS
        </div>
        <div className="border border-t-0 border-orange-400 rounded-b-md p-3 text-xs">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2">
            {(() => {
              const labels = [
                "ARMOUR",
                "BRIDGE", 
                "CARGO",
                "CREW",
                "FUEL",
                "HULL",
                "J-DRIVE",
                "M-DRIVE",
                "POWER PLANT",
                "SENSORS",
                "WEAPONS",
              ];
              
              return labels.map((label) => (
                <div key={label} className="flex items-center gap-3 min-w-0">
                  <span className="text-xs font-semibold text-gray-800 whitespace-nowrap min-w-0 flex-shrink-0">
                    {label}
                  </span>
                  <div className="flex gap-1 flex-shrink-0">
                    {[...Array(6)].map((_, i) => (
                      <span
                        key={i}
                        className="w-4 h-4 border border-gray-400 rounded-full inline-block"
                      />
                    ))}
                  </div>
                </div>
              ));
            })()}
          </div>
        </div>
      </div>

      {/* Deckplans Section */}
      <div className="mt-6 deckplans-section">
        <DeckPlanUpload />
      </div>

      {/* Breakaway Hulls Summary */}
      <div className="mt-6">
        <BreakawayHullsSummary />
      </div>
    </div>
  );
};
